import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { inr, dt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Radio, ArrowDownLeft, Zap, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const notes = [880, 1320];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = f;
      const t0 = ctx.currentTime + i * 0.12;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.14, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
      o.start(t0); o.stop(t0 + 0.24);
    });
    setTimeout(() => ctx.close(), 600);
  } catch (e) {
    console.debug("chime playback unavailable", e);
  }
}

export const LiveFeed = () => {
  const [items, setItems] = useState([]);
  const [flash, setFlash] = useState(null);
  const [live, setLive] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("wp_feed_mute") === "1");
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const esRef = useRef(null);

  useEffect(() => {
    let closed = false;

    api.get("/transactions", { params: { limit: 6 } })
      .then(({ data }) => { if (!closed) setItems(data.slice(0, 6)); })
      .catch(() => {});

    const es = new EventSource(`${API}/events`, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => !closed && setLive(true);
    es.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        if (!ev.id) return;
        setItems((prev) => [ev, ...prev.filter((p) => p.id !== ev.id)].slice(0, 6));
        setFlash(ev.id);
        setTimeout(() => setFlash((f) => (f === ev.id ? null : f)), 1800);

        if (ev.status === "success") {
          toast.success(`${inr(ev.amount)} received`, {
            description: `${ev.customer_name} · ${ev.vpa}`,
          });
          if (!mutedRef.current) playChime();
        }
      } catch (e) {
        console.debug("live feed message parse error", e);
      }
    };
    es.onerror = () => setLive(false);

    return () => { closed = true; es.close(); };
  }, []);

  const toggleMute = () => {
    setMuted((m) => {
      const n = !m;
      localStorage.setItem("wp_feed_mute", n ? "1" : "0");
      return n;
    });
  };

  return (
    <Card className="p-5" data-testid="live-feed">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          <ArrowDownLeft className="h-4 w-4 text-success" /> Live collections
        </h3>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs font-medium" data-testid="live-indicator">
            <span className="relative flex h-2 w-2">
              {live && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />}
              <span className={`relative inline-flex rounded-full h-2 w-2 ${live ? "bg-success" : "bg-muted-foreground"}`} />
            </span>
            <span className={live ? "text-success" : "text-muted-foreground"}>
              {live ? "LIVE · real-time" : "connecting…"}
            </span>
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleMute}
            data-testid="feed-mute-toggle" aria-label={muted ? "Unmute alerts" : "Mute alerts"}>
            {muted ? <VolumeX className="h-4 w-4 text-muted-foreground" /> : <Volume2 className="h-4 w-4 text-success" />}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
          <Zap className="h-5 w-5" /> Waiting for incoming payments…
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((t) => (
            <div
              key={t.id}
              data-testid={`feed-${t.order_id}`}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition-all ${
                flash === t.id ? "border-success/60 bg-success/5 animate-fade-up" : "border-border"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Radio className={`h-4 w-4 shrink-0 ${t.status === "success" ? "text-success" : "text-muted-foreground"}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.customer_name}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{t.vpa} · {dt(t.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-mono text-sm font-semibold">{inr(t.amount)}</span>
                <StatusBadge status={t.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
